import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injector, Input, OnInit, Type } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AnagraficaBaseService } from 'src/app/core/anagrafica-base.service';
import { AnagraficaIndirizziService } from 'src/app/core/anagrafica-indirizzi.service';
import { Indirizzo } from '../../models/indirizzo.model';

@Component({
  selector: 'app-anagrafica-editor',
  templateUrl: './anagrafica-editor.component.html',
  styleUrls: ['./anagrafica-editor.component.scss']
})
export class AnagraficaEditorComponent<TItem, TService extends AnagraficaBaseService<TItem>> implements OnInit {
  @Input() public serviceType!: Type<TService>;
  @Input() public itemTitle!: string;
  @Input() public nameProperty!: string;
  @Input() public filterRows!: (items: TItem[]) => TItem[];
  @Input() public canModified = true;
  @Input() public showAddButton = true;

  public selectedIndirizzi: Indirizzo[] = [];
  public editMode: 'new' | 'edit' | 'sync' | undefined;
  public form!: FormGroup;
  public items$!: Observable<TItem[]>;
  public itemInEdit: TItem | null = null;
  public isDialogOpen = false;
  public isIndirizzi = false;
  public selectIndirizzo!: Indirizzo;
  public showHideDialog = false;
  public indirizzoToHide: TItem | null = null;
  private service!: TService;

  constructor(
    private injector: Injector,
    private messageService: MessageService
  ) { }

  public ngOnInit(): void {

    this.service = this.injector.get(this.serviceType);
    this.isIndirizzi = this.service instanceof AnagraficaIndirizziService;
    if (this.filterRows) {
      this.items$ = this.service.getAll().pipe(map(i => this.filterRows(i)));
    } else {
      this.items$ = this.service.getAll();
    }

    this.form = new FormGroup({
      name: new FormControl(null, Validators.required)
    });
  }

  openSyncIndirizzi(): void {
    this.isDialogOpen = true;
    this.editMode = 'sync';
  }

  setFormSelect(): void {
    this.form.controls['name'].setValue(this.selectIndirizzo
      ? (this.selectIndirizzo as any)[this.nameProperty] : null);
  }

  public openModal(mode: 'new' | 'edit', item?: TItem): void {
    this.isDialogOpen = true;
    this.editMode = mode;
    this.itemInEdit = item || null;
    this.form.controls['name'].setValue(item ? (item as any)[this.nameProperty] : null);

    setTimeout(() => {
      document.getElementById('name')?.focus();
    }, 50);
  }

  public resetForm(): void {
    this.isDialogOpen = false;
    this.editMode = undefined;
    this.itemInEdit = null;
    this.form.reset();
  }

  public saveItem(): void {
    if (this.editMode === 'new') {
      const item = {
        [this.nameProperty]: this.form.controls['name'].value as string
      } as unknown as TItem;

      this.service.create(item).subscribe(
        () => this.handleSuccess('Creazione'),
        err => this.handleError(err)
      );
    } else if (this.editMode === 'sync') {
      const addressesToMerge = this.selectedIndirizzi.filter(address => address !== this.selectIndirizzo);

      addressesToMerge.forEach(address => {
        if (this.service instanceof AnagraficaIndirizziService) {
          this.service.cambiaindirizzo(address.indirizzoId, this.selectIndirizzo.indirizzoId).subscribe(
            () => this.handleSuccess('Raggruppato'),
            err => this.handleError(err)
          );
        }
      });
      this.selectedIndirizzi = [];
    } else {
      const item = { ...this.itemInEdit } as any;
      item[this.nameProperty] = this.form.controls['name'].value as string;

      this.service.update(item as TItem).subscribe(
        () => this.handleSuccess('Modifica'),
        err => this.handleError(err)
      );
    }
  }

  public removeItem(item: TItem): void {
    this.service.delete(item).subscribe({
      next: () => this.handleSuccess('Eliminazione'),
      error: (err: Error) => {
        if (err instanceof HttpErrorResponse && err.status === 409) {
          if (err.error?.canHide) {
            // Indirizzo confermato con documenti - chiedi se nascondere
            this.indirizzoToHide = item;
            this.showHideDialog = true;
          } else if (err.error?.mustConfirmOrAssociate) {
            // Indirizzo provvisorio con documenti - mostra solo messaggio
            this.message('error', 'Errore', err.error?.message || err.error);
          } else {
            this.message('error', 'Errore', err.error?.message || err.error);
          }
        } else {
          this.handleError(err);
        }
      }
    });
  }

  bloccaIndirizzo(item: Indirizzo): void {
    if (this.service instanceof AnagraficaIndirizziService) {
      item.bloccato = !item.bloccato;
      this.service.update(item).subscribe(
        () => this.handleSuccess('Modifica'),
        err => this.handleError(err)
      );
    }
  }

  public confirmHideIndirizzo(): void {
    if (this.indirizzoToHide && this.service instanceof AnagraficaIndirizziService) {
      this.service.hide(this.indirizzoToHide as unknown as Indirizzo).subscribe({
        next: () => {
          this.message('success', 'Nascosto', 'Indirizzo bloccato correttamente');
          this.showHideDialog = false;
          this.indirizzoToHide = null;
        },
        error: () => {
          this.message('error', 'Errore', 'Non è possibile nascondere l\'indirizzo');
          this.showHideDialog = false;
        }
      });
    }
  }

  public cancelHide(): void {
    this.showHideDialog = false;
    this.indirizzoToHide = null;
  }
  private handleSuccess(operation: string) {
    this.message('success', operation, `${operation} ${this.itemTitle} completata`);
    this.resetForm();
  }

  private handleError(err: Error): void {
    if (err instanceof HttpErrorResponse && err.status === 409) {
      this.message('error', 'Errore', err.error);
    } else {
      this.message('error', 'Errore', `Si è verificato un errore. Operazione non completata`);
      this.resetForm();
    }
  }

  private message(sev: string, sum: string, det: string) {
    this.messageService.add({
      key: 'toast',
      severity: sev,
      summary: sum,
      detail: det
    });
  }
}
